/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Tek kaynak qubepro-core; değişiklikler resmî sync ile uydulara yayılır.
 */

let authority = '';
let companyId: string | null = null;
let revision = 0;
let controller = new AbortController();
let workspaceMembershipId: string | null = null;
let workspaceBlocked = false;
const invalidators = new Set<() => void>();

export function getCompanyScopeSnapshot() {
  return { key: JSON.stringify([authority, companyId]), companyId, revision, signal: controller.signal, membershipId: workspaceMembershipId, blocked: workspaceBlocked };
}

export function setWorkspaceMembershipForRequests(value: string | null): void { workspaceMembershipId = value; }

/** Force a new generation even when company/authority are already empty (e.g. bootstrap or A→B→A). */
export function suspendWorkspaceRequests(): void {
  workspaceBlocked = true;
  workspaceMembershipId = null;
  authority = '';
  companyId = null;
  invalidateCompanyScope();
}

/** A successful explicit cookie login starts empty; CompanyProvider must still resolve firm authority. */
export function beginAuthenticatedWorkspace(): void {
  authority = ''; companyId = null; workspaceMembershipId = null;
  workspaceBlocked = false;
  invalidateCompanyScope();
}

export function invalidateCompanyScope(): void {
  revision += 1;
  const previous = controller;
  controller = new AbortController();
  previous.abort();
  invalidators.forEach(invalidate => invalidate());
}

export function isWorkspaceBoundRequest(value: string): boolean {
  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const url = new URL(value, origin);
    if (url.origin !== origin || !/\/api\//.test(url.pathname)) return false;
    return !/\/api\/auth\/(?:workspaces(?:\/|$)|workspace-context(?:\/|$)|workspace-capability(?:\/|$)|login(?:\/|$)|logout(?:\/|$)|refresh(?:\/|$)|two-factor(?:\/|$)|local(?:\/|$)|password-reset(?:\/|$))/.test(url.pathname);
  } catch { return false; }
}

export function assertWorkspaceRequestAllowed(snapshot: ReturnType<typeof getCompanyScopeSnapshot>): void {
  if (snapshot.blocked) throw new CompanyScopeChangedError();
  assertCompanyScopeCurrent(snapshot);
}

/** Called only after CompanyProvider validates the candidate scope. Same-scope locale refreshes are inert. */
export function transitionCompanyScope(nextCompanyId: string | null, nextAuthority = authority): void {
  if (companyId === nextCompanyId && authority === nextAuthority) return;
  companyId = nextCompanyId;
  authority = nextAuthority;
  invalidateCompanyScope();
}

export function registerCompanyScopeInvalidator(invalidate: () => void): () => void {
  invalidators.add(invalidate);
  return () => { invalidators.delete(invalidate); };
}

export class CompanyScopeChangedError extends Error {
  readonly code = 'ERR_CANCELED';
  readonly __CANCEL__ = true;
  constructor() {
    super('Company scope changed');
    this.name = 'CanceledError';
  }
}

export function assertCompanyScopeCurrent(snapshot: ReturnType<typeof getCompanyScopeSnapshot>): void {
  if (snapshot.revision !== revision || snapshot.signal.aborted) throw new CompanyScopeChangedError();
}

/** Keep the caller's cancellation and the company transition cancellation. */
export function combineCompanyScopeSignal(scopeSignal: AbortSignal, callerSignal?: AbortSignal | null): AbortSignal {
  return callerSignal ? AbortSignal.any([scopeSignal, callerSignal]) : scopeSignal;
}

/** Headers may arrive before a switch while JSON/blob decoding completes afterwards. */
export function guardCompanyScopeResponse(response: Response, snapshot: ReturnType<typeof getCompanyScopeSnapshot>): Response {
  const readers = new Set(['json', 'text', 'blob', 'arrayBuffer', 'formData', 'bytes']);
  return new Proxy(response, {
    get(target, key) {
      if (key === 'clone') return () => {
        assertCompanyScopeCurrent(snapshot);
        return guardCompanyScopeResponse(target.clone(), snapshot);
      };
      const value = Reflect.get(target, key, target);
      if (readers.has(String(key)) && typeof value === 'function') return async (...args: unknown[]) => {
        assertCompanyScopeCurrent(snapshot);
        const result = await value.apply(target, args);
        assertCompanyScopeCurrent(snapshot);
        return result;
      };
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}
